import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalR } from './use-signalr';
import type { Device } from '../types/device';

interface UseDeviceUpdatesOptions {
  /**
   * Called for every DeviceUpdated message, after the query cache has been patched.
   * <p>
   * The payload used to be consumed internally and thrown away, so a caller could know only that the
   * socket was connected. The guided first-run flow needs the device itself — it advances when a
   * machine first reports in — and polling for that would mean asking the server on a timer for an
   * event the server is already pushing.
   */
  onDeviceUpdated?: (device: Device) => void;
}

export function useDeviceUpdates(options: UseDeviceUpdatesOptions = {}) {
  const queryClient = useQueryClient();
  const { connection, status } = useSignalR({ hubPath: '/bff/hubs/devices' });

  // Held in a ref so a caller passing an inline arrow does not re-run the effect and tear down the
  // subscription on every render. Same reason useOrgApproval does it.
  const onDeviceUpdatedRef = React.useRef(options.onDeviceUpdated);
  onDeviceUpdatedRef.current = options.onDeviceUpdated;

  React.useEffect(() => {
    if (!connection) return;

    const handleDeviceUpdated = (device: Device) => {
      if (device.status === 'Removed') {
        queryClient.setQueryData<Device[]>(['devices'], (old) =>
          old ? old.filter((d) => d.id !== device.id) : old,
        );
        queryClient.invalidateQueries({ queryKey: ['devices', 'pending'] });
        onDeviceUpdatedRef.current?.(device);
        return;
      }

      queryClient.setQueryData<Device[]>(['devices'], (old) => {
        if (!old) return old;
        const idx = old.findIndex((d) => d.id === device.id);
        if (idx >= 0) {
          const updated = [...old];
          const existing = old[idx];
          updated[idx] = {
            ...existing,
            status: device.status,
            hostname: device.hostname || existing.hostname,
            platform: device.platform || existing.platform,
            agentVersion: device.agentVersion || existing.agentVersion,
            lastHeartbeatAt: device.status === 'Offline' ? null : (device.lastHeartbeatAt ?? existing.lastHeartbeatAt),
          };
          return updated;
        }
        if (device.status !== 'PendingApproval') {
          return [device, ...old];
        }
        return old;
      });

      if (['PendingApproval', 'Approved', 'Rejected'].includes(device.status)) {
        queryClient.invalidateQueries({ queryKey: ['devices', 'pending'] });
      }

      queryClient.invalidateQueries({ queryKey: ['devices', device.id] });

      onDeviceUpdatedRef.current?.(device);
    };

    connection.on('DeviceUpdated', handleDeviceUpdated);
    return () => {
      connection.off('DeviceUpdated', handleDeviceUpdated);
    };
  }, [connection, queryClient]);

  return { connectionStatus: status };
}
